import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Icon,
    useDisclosure,
    Switch,
    Divider,
    Button,
    Checkbox,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from '@chakra-ui/react';
import { TbLayoutList, TbListDetails, TbColumns, TbArrowBackUp, TbGripVertical, TbLayoutGrid } from 'react-icons/tb';
import { TableDensity, TableSettings, TableViewMode } from '../hooks/useTableSettings';
import { TabConfigItem, ALL_TABS } from '../hooks/useTabConfig';
import { useState, useEffect } from 'react';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AppDrawer from './AppDrawer';
import { Icon as YaxleIcon, IconButton, Tooltip } from '../ui';

interface TableSettingsButtonProps {
    settings: TableSettings;
    onUpdateSettings: (newSettings: Partial<TableSettings>) => void;
    hasCustomColumnWidths?: boolean;
    onResetColumnWidths?: () => void;
    tabConfig?: TabConfigItem[];
    onUpdateTabConfig?: (config: TabConfigItem[]) => void;
    rekeyingEnabled?: boolean;
    /** When true, show List / Grid view mode options (inventory). */
    showViewMode?: boolean;
    /** Drawer header title. Defaults to "Table Settings". */
    panelTitle?: string;
    /** Extra content at end of drawer body. Function form receives closePanel to dismiss the drawer. */
    footerSections?:
    | React.ReactNode
    | ((api: { closePanel: () => void; }) => React.ReactNode);
    /** Controlled open state, for callers that reopen the panel (e.g. Back from a sub-page). */
    isOpen?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    /** Open/close the drawer without animation (sub-page swaps). See `AppDrawer`. */
    instantMotion?: boolean;
}

const DENSITY_OPTIONS: { key: TableDensity; label: string; icon: typeof TbLayoutList; description?: string; }[] = [
    { key: 'compact', label: 'Compact', icon: TbLayoutList },
    { key: 'detailed', label: 'Detailed', icon: TbListDetails },
];

const VIEW_MODE_OPTIONS: { key: TableViewMode; label: string; icon: typeof TbLayoutList; description: string; }[] = [
    { key: 'grid', label: 'Grid', icon: TbLayoutGrid, description: 'Card layout with images' },
    { key: 'list', label: 'List', icon: TbLayoutList, description: 'Traditional table rows' },
];

interface SortableTabRowProps {
    item: TabConfigItem;
    onToggleVisible: (value: string) => void;
    rekeyingEnabled?: boolean;
}

const SortableTabRow = ({ item, onToggleVisible, rekeyingEnabled }: SortableTabRowProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.value });

    const tabDef = ALL_TABS.find(t => t.value === item.value);
    const isDisabledByRekeying = tabDef?.requiresRekeying && !rekeyingEnabled;

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : undefined,
    };

    return (
        <Flex
            ref={setNodeRef}
            style={style}
            align="center"
            gap={2}
            py={2}
            px={2}
            borderRadius="md"
            _hover={{ bg: 'gray.50' }}
        >
            <Checkbox
                isChecked={item.visible}
                isDisabled={!!isDisabledByRekeying}
                onChange={() => onToggleVisible(item.value)}
                flexShrink={0}
                colorScheme="blue"
            />
            <Text fontSize="sm" flex={1} noOfLines={1} color={isDisabledByRekeying ? 'gray.400' : 'gray.700'}>
                {tabDef?.label ?? item.value}
            </Text>
            <Box
                {...attributes}
                {...listeners}
                cursor="grab"
                color="gray.400"
                _hover={{ color: 'gray.600' }}
                flexShrink={0}
                display="flex"
                alignItems="center"
            >
                <Icon as={TbGripVertical as React.ElementType} boxSize={4} />
            </Box>
        </Flex>
    );
};

const TableSettingsButton = ({
    settings,
    onUpdateSettings,
    hasCustomColumnWidths,
    onResetColumnWidths,
    tabConfig,
    onUpdateTabConfig,
    rekeyingEnabled,
    showViewMode,
    panelTitle = 'Table Settings',
    footerSections,
    isOpen: controlledIsOpen,
    onOpen: controlledOnOpen,
    onClose: controlledOnClose,
    instantMotion,
}: TableSettingsButtonProps) => {
    const disclosure = useDisclosure();
    const isOpen = controlledIsOpen ?? disclosure.isOpen;
    const onOpen = controlledOnOpen ?? disclosure.onOpen;
    const onClose = controlledOnClose ?? disclosure.onClose;

    const [draftSettings, setDraftSettings] = useState<TableSettings>(settings);
    const [draftTabConfig, setDraftTabConfig] = useState<TabConfigItem[]>(tabConfig ?? []);
    const [pendingResetColumnWidths, setPendingResetColumnWidths] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setDraftSettings(settings);
            setDraftTabConfig(tabConfig ?? []);
            setPendingResetColumnWidths(false);
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDiscard = () => {
        setDraftSettings(settings);
        setDraftTabConfig(tabConfig ?? []);
        setPendingResetColumnWidths(false);
        onClose();
    };

    const handleSave = () => {
        onUpdateSettings(draftSettings);
        if (onUpdateTabConfig && tabConfig !== undefined) {
            onUpdateTabConfig(draftTabConfig);
        }
        if (pendingResetColumnWidths && onResetColumnWidths) {
            onResetColumnWidths();
        }
        onClose();
    };

    const handleToggleVisible = (value: string) => {
        setDraftTabConfig(prev =>
            prev.map(item => item.value === value ? { ...item, visible: !item.visible } : item)
        );
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setDraftTabConfig(prev => {
                const oldIndex = prev.findIndex(t => t.value === active.id);
                const newIndex = prev.findIndex(t => t.value === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    return (
        <>
            <Tooltip label={panelTitle} placement="bottom" hasArrow>
                <IconButton
                    aria-label={panelTitle}
                    icon={<YaxleIcon name="settings" size="sm" />}
                    size="toolbar"
                    variant="ghost"
                    colorScheme="gray"
                    color="fg.muted"
                    onClick={onOpen}
                />
            </Tooltip>

            <AppDrawer
                isOpen={isOpen}
                onClose={handleDiscard}
                size="sm"
                title={panelTitle}
                titleAddon={<YaxleIcon name="settings" size="lg" color="gray.600" />}
                instantMotion={instantMotion}
                footer={
                    <>
                        <Button size="action" variant="outlineGray" onClick={handleDiscard}>
                            Cancel
                        </Button>
                        <Button
                            size="action"
                            bg="#0071E3"
                            color="white"
                            onClick={handleSave}
                            fontWeight="medium"
                            _hover={{ bg: '#3544a0' }}
                        >
                            Save
                        </Button>
                    </>
                }
            >
                {showViewMode && (
                    <Box mb={6}>
                        <Text fontSize="md" fontWeight="bold" color="gray.900" mb={4}>
                            View
                        </Text>
                        <VStack spacing={3} align="stretch">
                            {VIEW_MODE_OPTIONS.map((option) => {
                                const isSelected =
                                    (draftSettings.viewMode ?? 'list') === option.key;
                                return (
                                    <Box
                                        key={option.key}
                                        as="button"
                                        type="button"
                                        onClick={() =>
                                            setDraftSettings((prev) => ({
                                                ...prev,
                                                viewMode: option.key,
                                            }))
                                        }
                                        border="1px solid"
                                        borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                        borderRadius="lg"
                                        px={4}
                                        py={3}
                                        cursor="pointer"
                                        bg={isSelected ? '#E6F1FD' : 'white'}
                                        _hover={{
                                            borderColor: isSelected ? '#0071E3' : 'gray.300',
                                            bg: isSelected ? '#E6F1FD' : 'gray.50',
                                        }}
                                        transition="all 0.15s"
                                        textAlign="left"
                                        w="100%"
                                    >
                                        <HStack spacing={3}>
                                            <Flex
                                                align="center"
                                                justify="center"
                                                w="20px"
                                                h="20px"
                                                borderRadius="full"
                                                border="2px solid"
                                                borderColor={
                                                    isSelected ? '#0071E3' : 'gray.300'
                                                }
                                                flexShrink={0}
                                            >
                                                {isSelected && (
                                                    <Box
                                                        w="10px"
                                                        h="10px"
                                                        borderRadius="full"
                                                        bg="#0071E3"
                                                    />
                                                )}
                                            </Flex>
                                            <Icon
                                                as={option.icon as React.ElementType}
                                                boxSize={5}
                                                color={isSelected ? '#0071E3' : 'gray.500'}
                                            />
                                            <Box>
                                                <Text
                                                    fontSize="sm"
                                                    fontWeight="semibold"
                                                    color="gray.800"
                                                >
                                                    {option.label}
                                                </Text>
                                                <Text fontSize="xs" color="gray.500">
                                                    {option.description}
                                                </Text>
                                            </Box>
                                        </HStack>
                                    </Box>
                                );
                            })}
                        </VStack>
                    </Box>
                )}
                {/* Table Density Section */}
                <Box mb={6}>
                    <Text fontSize="md" fontWeight="bold" color="gray.900" mb={4}>
                        Table Density
                    </Text>

                    <VStack spacing={3} align="stretch">
                        {DENSITY_OPTIONS.map(option => {
                            const isSelected = draftSettings.density === option.key;
                            return (
                                <Box
                                    key={option.key}
                                    as="button"
                                    type="button"
                                    onClick={() => setDraftSettings(prev => ({ ...prev, density: option.key }))}
                                    border="1px solid"
                                    borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                    borderRadius="lg"
                                    px={4}
                                    py={3}
                                    cursor="pointer"
                                    bg={isSelected ? '#E6F1FD' : 'white'}
                                    _hover={{
                                        borderColor: isSelected ? '#0071E3' : 'gray.300',
                                        bg: isSelected ? '#E6F1FD' : 'gray.50',
                                    }}
                                    transition="all 0.15s"
                                    textAlign="left"
                                    w="100%"
                                >
                                    <HStack spacing={3}>
                                        <Flex
                                            align="center"
                                            justify="center"
                                            w="20px"
                                            h="20px"
                                            borderRadius="full"
                                            border="2px solid"
                                            borderColor={isSelected ? '#0071E3' : 'gray.300'}
                                            flexShrink={0}
                                        >
                                            {isSelected && (
                                                <Box
                                                    w="10px"
                                                    h="10px"
                                                    borderRadius="full"
                                                    bg="#0071E3"
                                                />
                                            )}
                                        </Flex>
                                        <Icon
                                            as={option.icon as React.ElementType}
                                            boxSize={5}
                                            color={isSelected ? '#0071E3' : 'gray.500'}
                                        />
                                        <Text
                                            fontSize="sm"
                                            fontWeight="medium"
                                            color={isSelected ? '#0071E3' : 'gray.700'}
                                        >
                                            {option.label}
                                        </Text>
                                    </HStack>
                                </Box>
                            );
                        })}
                    </VStack>
                </Box>

                <Divider mb={6} />

                {/* Column Dividers Section */}
                <Flex
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={() => setDraftSettings(prev => ({ ...prev, columnDividers: !prev.columnDividers }))}
                >
                    <HStack spacing={3}>
                        <Icon
                            as={TbColumns as React.ElementType}
                            boxSize={5}
                            color="gray.500"
                        />
                        <Box>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                Column Dividers
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                                Display vertical dividers between each column
                            </Text>
                        </Box>
                    </HStack>
                    <Switch
                        isChecked={draftSettings.columnDividers}
                        pointerEvents="none"
                        colorScheme="blue"
                        size="md"
                    />
                </Flex>

                {(hasCustomColumnWidths || pendingResetColumnWidths) && onResetColumnWidths && (
                    <>
                        <Divider my={6} />
                        <Flex
                            align="center"
                            justify="space-between"
                            cursor="pointer"
                            onClick={() => setPendingResetColumnWidths(true)}
                            _hover={{ bg: 'gray.50' }}
                            borderRadius="md"
                            p={2}
                            mx={-2}
                            bg={pendingResetColumnWidths ? 'blue.50' : undefined}
                        >
                            <HStack spacing={3}>
                                <Icon
                                    as={TbArrowBackUp as React.ElementType}
                                    boxSize={5}
                                    color="gray.500"
                                />
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                        Reset Column Widths
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        Restore all columns to their default widths
                                    </Text>
                                </Box>
                            </HStack>
                        </Flex>
                    </>
                )}

                {/* Tab Configuration Section */}
                {tabConfig !== undefined && (
                    <Box mt={6}>
                        <Accordion allowToggle defaultIndex={[0]}>
                            <Box
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="lg"
                                overflow="visible"
                            >
                                <AccordionItem border="none">
                                    <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                        <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                            Tab Configuration
                                        </Text>
                                        <AccordionIcon />
                                    </AccordionButton>
                                    <AccordionPanel px={4} pb={4} pt={1}>
                                        {/* Hint text */}
                                        <Box
                                            mb={3}
                                            px={3}
                                            py={2.5}
                                            bg="gray.50"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                        >
                                            <Text fontSize="xs" color="gray.500">
                                                Drag rows to reorder. Uncheck to hide a tab.
                                            </Text>
                                        </Box>

                                        {/* "All" row — locked/pinned */}
                                        <Flex
                                            align="center"
                                            gap={2}
                                            py={2}
                                            px={2}
                                            borderRadius="md"
                                            opacity={0.45}
                                        >
                                            <Checkbox isChecked isDisabled flexShrink={0} colorScheme="blue" />
                                            <Text fontSize="sm" flex={1} noOfLines={1} color="gray.700">
                                                All
                                            </Text>
                                        </Flex>

                                        {/* Sortable tab rows */}
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={draftTabConfig.map(t => t.value)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {draftTabConfig.map(item => (
                                                    <SortableTabRow
                                                        key={item.value}
                                                        item={item}
                                                        onToggleVisible={handleToggleVisible}
                                                        rekeyingEnabled={rekeyingEnabled}
                                                    />
                                                ))}
                                            </SortableContext>
                                        </DndContext>
                                    </AccordionPanel>
                                </AccordionItem>
                            </Box>
                        </Accordion>
                    </Box>
                )}
                {typeof footerSections === 'function'
                    ? footerSections({ closePanel: handleDiscard })
                    : footerSections}
            </AppDrawer>
        </>
    );
};

export default TableSettingsButton;
